import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import de from "./locale/de/translations.json";
import en from "./locale/en/translations.json";
import es from "./locale/es/translations.json";
import fr from "./locale/fr/translations.json";
import it from "./locale/it/translations.json";
import pl from "./locale/pl/translations.json";

export const SUPPORTED_LANGUAGES = [
  "de",
  "en",
  "es",
  "fr",
  "it",
  "pl",
] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const isSupportedLanguage = (
  lang: string,
): lang is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
};

const resources = {
  de: { translation: de },
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  it: { translation: it },
  pl: { translation: pl },
};

// Detect browser language without using React hooks
export const detectBrowserLanguage = (): string => {
  // Try to get saved language from localStorage directly
  // Assuming your settings atom uses 'settings' as the localStorage key
  try {
    const savedSettings = localStorage.getItem("settings");
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      if (settings?.language) {
        return settings.language;
      }
    }
  } catch (error) {
    console.warn("Failed to read language from localStorage:", error);
  }

  // Fall back to browser language detection
  const browserLang = navigator.language || navigator.languages?.[0] || "en";
  const langCode = browserLang.split("-")[0].toLowerCase();

  // Return if supported, otherwise fallback to 'en'
  return isSupportedLanguage(langCode) ? langCode : "en";
};

i18n.use(initReactI18next).init({
  resources,
  lng: detectBrowserLanguage(),
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
  react: {
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: [
      "br",
      "strong",
      "em",
      "i",
      "p",
      "ul",
      "li",
      "a",
      "sup",
    ],
    useSuspense: false,
    // react i18next special options (optional)
    // override if needed - omit if ok with defaults
    /*
      bindI18n: 'languageChanged',
      bindI18nStore: '',
      transEmptyNodeValue: '',

    */
  },
  detection: {
    order: ["localStorage", "navigator"],
    lookupLocalStorage: "baiki-language",
    caches: ["localStorage"],
  },
});

export default i18n;
